import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { supabase } from "@/shared/lib/supabase";
import { ESTATE_MODULES } from "@/shared/constants/estateModules";
import { MODULE_DEFINITIONS } from "@/shared/constants/moduleDefinitions";
import { useUserEstateRole } from "@/shared/hooks/useUserEstateRole";
import type { Database } from "@/shared/types/database.types";
import type { PaymentTierStatus } from "@/shared/hooks/usePaymentTier";

type EstateRow = Database["public"]["Tables"]["estates"]["Row"];
type UserRow = Database["public"]["Tables"]["users"]["Row"];

export interface IntercomCustomAttributes {
  deceased_first_name: string | null;
  relationship_to_deceased: string | null;
  legal_role: string | null;
  date_of_death: string | null;
  current_roadmap_stage: string | null;
  documents_uploaded: string | null;
  plan_type: string | null;
  subscription_status: string | null;
  last_active_on: string | null;
  last_page_viewed: string | null;
  is_invited_member: string | null;
  roadmap_progress: string | null;
}

interface UseIntercomAttributesProps {
  person: EstateRow | null;
  user: UserRow | null;
  subscription: PaymentTierStatus;
}

function computeActiveModuleTitle(taskStatusMap: Map<string, string>): string {
  for (const def of MODULE_DEFINITIONS) {
    const estateModule = ESTATE_MODULES.find((m) => m.id === def.id);
    const tasks = estateModule?.tasks ?? [];

    if (tasks.length === 0) return def.title;

    const allCompleted = tasks.every((t) => taskStatusMap.get(t.taskId) === "completed");

    if (!allCompleted) return def.title;
  }

  return "Completed";
}

function computeRoadmapProgress(taskStatusMap: Map<string, string>): string {
  return MODULE_DEFINITIONS.map((def) => {
    const estateModule = ESTATE_MODULES.find((m) => m.id === def.id);
    const tasks = estateModule?.tasks ?? [];

    if (tasks.length === 0) {
      return `${def.title}: Not Started`;
    }

    const allCompleted = tasks.every((t) => taskStatusMap.get(t.taskId) === "completed");

    if (allCompleted) return `${def.title}: Completed`;

    const anyStarted = tasks.some(
      (t) => taskStatusMap.has(t.taskId) && taskStatusMap.get(t.taskId) !== "not_started",
    );

    return anyStarted ? `${def.title}: In Progress` : `${def.title}: Not Started`;
  }).join(", ");
}

function computePlanType(user: UserRow | null, subscription: PaymentTierStatus): string {
  if (user?.requested_deletion) return "requested_account_deletion";
  if (subscription.isActive) return "paid";
  return "free";
}

export function useIntercomAttributes({
  person,
  user,
  subscription,
}: UseIntercomAttributesProps): IntercomCustomAttributes | null {
  const estateId = person?.estate_id;

  const { legalRole, relationshipToDecedent } = useUserEstateRole();

  const { data: documentTypes } = useQuery({
    queryKey: ["intercom-documents", estateId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("estate_documents")
        .select("document_type")
        .eq("estate_id", estateId!);
      if (error) throw error;
      const types = [...new Set((data ?? []).map((d) => d.document_type))];
      return types;
    },
    enabled: !!estateId,
    staleTime: 5 * 60 * 1000,
  });

  const { data: roadmapData } = useQuery({
    queryKey: ["intercom-roadmap-stage", estateId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("estate_tasks")
        .select("task_id, status")
        .eq("estate_id", estateId!);

      if (error) throw error;

      const taskStatusMap = new Map((data ?? []).map((r) => [r.task_id, r.status]));

      return {
        activeModuleTitle: computeActiveModuleTitle(taskStatusMap),
        roadmapProgress: computeRoadmapProgress(taskStatusMap),
      };
    },
    enabled: !!estateId,
    staleTime: 5 * 60 * 1000,
  });

  const planType = computePlanType(user, subscription);

  return useMemo(() => {
    if (!person) return null;

    const docs = documentTypes?.length ? documentTypes.join(", ") : null;

    return {
      deceased_first_name: person.first_name || null,
      relationship_to_deceased: relationshipToDecedent || null,
      legal_role: legalRole === "none" ? null : legalRole,
      date_of_death: person.date_of_death || null,
      current_roadmap_stage: roadmapData?.activeModuleTitle || null,
      documents_uploaded: docs,
      plan_type: planType,
      subscription_status: subscription.tier || null,
      last_active_on: user?.last_active_on || null,
      last_page_viewed: user?.last_page_viewed || null,
      is_invited_member:
        person.owner_user_id && user?.user_id
          ? String(user.user_id !== person.owner_user_id)
          : null,
      roadmap_progress: roadmapData?.roadmapProgress || null,
    };
  }, [
    person,
    legalRole,
    relationshipToDecedent,
    roadmapData,
    documentTypes,
    planType,
    subscription.tier,
    user?.last_active_on,
    user?.last_page_viewed,
    user?.user_id,
  ]);
}
